/**
 * @typedef {Object} AnimationDefsMap
 * @property {CompAnimationDefs} <compId>
 *
 * @typedef {Object} CompAnimationDefs
 * @property {AnimationDef[]} <action>
 *
 * @typedef {Object} AnimationDef
 * @property {string} targetId
 * @property {string} action
 * @property {string} name
 * @property {number} duration
 * @property {number} delay
 * @property {Object} params
 *
 * @typedef {Object} TriggerDescriptor
 * @property {string} compId
 * @property {string} action
 */
import { Actions, AnimationDef, LocalState, SessionState, TriggerType } from '../types'
import { AnimatorManager } from 'feature-animations'

const CLEAR_PROPS = 'clip,clipPath,webkitClipPath,willChange,opacity,transform,transformOrigin'
const getStateId = (id: string) => `${id}-screenIn`

/**
 * @class AnimationManager
 */
export class ScreenInManager {
	private animator: AnimatorManager
	private definitions: Actions
	private sessionState: SessionState
	readonly localState: LocalState

	constructor(animationsManager: AnimatorManager) {
		this.animator = animationsManager
		/** @type AnimationDefsMap*/
		this.definitions = {}
		/**
		 * State persistent across pages
		 * @type {{running: Map<TimelineMax, {id: string, action: string}>, played: Map<string, {playOnce: boolean, persistOnNav: boolean}>}}
		 */
		this.sessionState = {
			played: new Map(),
			running: new Map(),
		}
		/**
		 * State for current page
		 * @type {{played: Map<string, {targetId: string, id: string}>}}
		 */
		this.localState = {
			played: new Map(),
		}
	}

	_shouldSkipPlayedAnimation(stateId: string) {
		const wasPlayedLocally = this.localState.played.has(stateId)
		const wasPlayedInSession = this.sessionState.played.has(stateId)
		const { playOnce, persistOnNav } = this.sessionState.played.get(stateId) || {}
		return wasPlayedLocally || (wasPlayedInSession && (playOnce || persistOnNav))
	}

	_hideComponent(compId: string) {
		const element: any = document.querySelector(`#${compId}`)
		if (element) {
			element.style.opacity = 0
		}
	}

	_unhideComponent(compId: string) {
		const element: any = document.querySelector(`#${compId}`)
		if (element) {
			element.style.opacity = ''
			element.style.visibility = 'inherit'
		}
	}

	/**
	 * Save a copy of all animation definitions
	 * @param {AnimationDefsMap} animationDefsMap
	 * {
	 *     [compId]: [{
	 *             targetId: compId,
	 *             name: 'FadeIn',
	 *             action: 'screenIn',
	 *             duration: 1,
	 *             delay: 0,
	 *             params: {...}
	 *         }, ...],
	 *     ...
	 *     ...
	 * }
	 */

	updateDefinitions(animationDefsMap: Actions) {
		this.definitions = { ...this.definitions, ...animationDefsMap }
	}

	getDefinitions() {
		return this.definitions
	}

	_hideCompBeforeAnimation(compId: string) {
		const stateId = getStateId(compId)

		if (this._shouldSkipPlayedAnimation(stateId)) {
			this._unhideComponent(compId)
		} else {
			this._hideComponent(compId)
		}
	}

	/**
	 * Hide components that should be animated
	 */
	hideBeforeAnimation() {
		// Handle screen in hide on start
		const compsToHide = this.getCompsToHide()
		compsToHide.forEach(({ compId }) => {
			this._hideCompBeforeAnimation(compId)
		})
	}

	/**
	 * Trigger screenIn on all trigger descriptors passed.
	 * @param {TriggerDescriptor[]} triggers
	 */
	trigger(triggers: Array<TriggerType> = []) {
		const animationDefs = triggers.reduce((acc: Array<any>, { compId }) => {
			const actionDefs: Array<AnimationDef> = this.definitions[compId] || []
			return acc.concat(actionDefs)
		}, [])
		animationDefs.forEach(({ action, ...animation }) => this.executeAnimation(action, animation))
	}

	init(id: string) {
		this.stopAnimations({ idToKeep: id })
		this.clearLocalStore(id)

		this.hideBeforeAnimation()
	}

	/**
	 * Run an animation by action defs, each action has different pre-requisites and post-requisites
	 * trying only screenIn now
	 * @param action
	 * @param name
	 * @param targetId
	 * @param pageId
	 * @param duration
	 * @param playOnce
	 * @param persistOnNav
	 * @param delay
	 * @param params
	 */
	executeAnimation(
		action: string,
		{
			name,
			targetId,
			pageId,
			duration = 0,
			delay = 0,
			playOnce = false,
			persistOnNav = false,
			params = {},
		}: AnimationDef
	) {
		const stateId = getStateId(targetId)
		// Skip if played in current page or played in current session and marked as play once
		if (this._shouldSkipPlayedAnimation(stateId)) {
			return
		}

		const clearParams = { props: CLEAR_PROPS, immediateRender: false }
		const animationData = { name, targetId, duration, delay, params }
		const baseClearData = {
			name: 'BaseClear',
			targetId,
			duration: 0,
			delay: 0,
			params: clearParams,
		}

		this.animator.runSequence(
			{ animations: [animationData, baseClearData] },
			{
				onStart: (instance: any) => this.sessionState.running.set(instance, { targetId, action, id: pageId }),
				onComplete: (instance: any) => this.sessionState.running.delete(instance),
				onInterrupt: (instance: any) => this.sessionState.running.delete(instance),
			}
		)

		this.localState.played.set(stateId, { targetId, id: pageId })
		this.sessionState.played.set(stateId, { playOnce, persistOnNav })
	}

	/**
	 * kill all running screenIn animations
	 * @param {boolean} [skipPersistent=true] skip screenIn marked as persistent (like masterPage screenIn)
	 * @param {string} idToKeep
	 */
	stopAnimations({ skipPersistent = true, idToKeep }: { skipPersistent?: boolean; idToKeep?: string } = {}) {
		this.sessionState.running.forEach(({ targetId, id }: { targetId: string; id: string }, animation: any) => {
			const stateId = getStateId(targetId)
			// If animation is marked as persistent (ie. master page)
			const isPersistentToSkip = skipPersistent && this.sessionState.played.get(stateId).persistOnNav
			if (!isPersistentToSkip && idToKeep !== id) {
				this.animator.kill(animation, 1)
			}
		})
	}

	/**
	 * Clear temp (page) played screenIn list
	 * @param {string} idToKeep
	 */
	clearLocalStore(idToKeep: string) {
		if (this.localState) {
			this.localState.played.forEach(({ targetId, id }: { targetId: string; id: string }, stateId: string) => {
				if (!this.definitions[targetId] || idToKeep !== id) {
					this.localState.played.delete(stateId)
				}
			})
		}
	}

	getAnimationProperties(animationName: string) {
		return this.animator.getAnimationProperties(animationName)
	}

	getCompsToHide(): Array<{ compId: string }> {
		return Object.entries(this.definitions).reduce((acc: any, [compId, behaviors]) => {
			const hide = behaviors.some(({ name }) => {
				const animationProperties = this.animator.getAnimationProperties(name)
				return animationProperties && animationProperties.hideOnStart
			})
			if (hide) {
				acc.push({ compId })
			}

			return acc
		}, [])
	}
}
